import React, { useEffect, useState } from "react";
import { Col, Placeholder, Row } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";

import DstButton from "../../components/DstButton/DstButton";
import DstConfirm from "../../components/DstConfirm/DstConfirm";
import InfoRow from "../../components/InfoRow/InfoRow";
import { useSession } from "../../contexts/SessionContext";
import translate from "../../services/Translate";
import { completeNavigate } from "../../services/UtilityService";
import {
  deleteProjectSubjectResource,
  fetchProjectSubjectDetails,
  fetchProjectSubjectResources,
} from "./ProjectSubject.function";
import EditProjectSubjectModal from "./components/EditProjectSubjectModal/EditProjectSubjectModal";
import EditProjectSubjectResourceModal from "./components/EditProjectSubjectResourceModal/EditProjectSubjectResourceModal";

const ProjectSubject = () => {
  const { subject_id } = useParams();
  const { language } = useSession();
  const navigate = useNavigate();

  const [projectSubject, setProjectSubject] = useState<any>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [resources, setResources] = useState<any[]>([]);
  const [resourcesLoaded, setResourcesLoaded] = useState(false);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [editingResource, setEditingResource] = useState<any>(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [resourceToDelete, setResourceToDelete] = useState<number | null>(null);

  useEffect(() => {
    if (!subject_id) return;

    fetchProjectSubjectDetails(Number(subject_id), setProjectSubject, setIsLoaded, language);
    fetchProjectSubjectResources(Number(subject_id), setResources, setResourcesLoaded, language);
  }, [subject_id, language]);

  if (!isLoaded) {
    return (
      <div className="container-fluid mt-3 mb-5 px-3 px-md-4">
        <Placeholder animation="glow">
          <Placeholder xs={6} />
          <Placeholder xs={8} />
          <Placeholder xs={4} />
        </Placeholder>
      </div>
    );
  }

  if (!projectSubject) {
    return (
      <div className="text-center mt-3">
        <h2 className="mb-3">{translate(language, "PAGES.PROJECT_SUBJECT.MAIN.ERROR.NOT_FOUND")}</h2>
        <DstButton
          value={translate(language, "PAGES.PROJECT_SUBJECT.MAIN.PROJECT_SUBJECTS_LIST")}
          clickFunction={() => navigate("/projects/subjects")}
        />
      </div>
    );
  }

  const _handleDeleteResource = (resourceId: number) => {
    setResourceToDelete(resourceId);
    setShowConfirmDelete(true);
  };

  const _confirmDeleteResource = () => {
    if (!subject_id || !resourceToDelete) return;

    deleteProjectSubjectResource(
      Number(subject_id),
      resourceToDelete,
      () => {
        setResources((prev) => prev.filter((resources) => resources.id !== resourceToDelete));
        setShowConfirmDelete(false);
        setResourceToDelete(null);
      },
      language
    );
  };

  return (
    <div className="container-fluid mt-3 mb-5 px-3 px-md-4">
      <Helmet>
        <title>Project subject – {projectSubject.name}</title>
      </Helmet>

      <div className="d-flex align-items-center mb-3">
        <span
          className="dst-icon-chevron-left p-2 medium-gray-background rounded me-2 clickable"
          onClick={(event) => completeNavigate(event, navigate, "/projects/subjects")}
        />
        <h1 className="mb-0 h3 me-3">{projectSubject.name}</h1>
      </div>

      <Row>
        <Col md={6}>
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h3 className="mb-0 m-bold">{translate(language, "PAGES.PROJECT_SUBJECT.MAIN.INFORMATION.TITLE")}</h3>
            <DstButton
              variant="secondary"
              value={translate(language, "PAGES.PROJECT_SUBJECT.MAIN.EDIT")}
              btnImageBefore={<span className="dst-icon-edit-02 me-2" />}
              clickFunction={() => setShowEditModal(true)}
            />
          </div>

          {InfoRow(
            "dst-icon-tag-01",
            translate(language, "PAGES.PROJECT_SUBJECT.MAIN.SHORT_NAME"),
            projectSubject.short_name
          )}
          {InfoRow(
            "dst-icon-globe-05",
            translate(language, "PAGES.PROJECT_SUBJECT.MAIN.LANGUAGE"),
            projectSubject.language
          )}
          {InfoRow(
            "dst-icon-check-circle",
            translate(language, "PAGES.PROJECT_SUBJECT.MAIN.STATUS"),
            projectSubject.status
          )}
          {InfoRow(
            "dst-icon-bar-chart-01",
            translate(language, "PAGES.PROJECT_SUBJECT.MAIN.DIFFICULTY"),
            projectSubject.difficulty
          )}
          {InfoRow(
            "dst-icon-clipboard",
            translate(language, "PAGES.PROJECT_SUBJECT.MAIN.CURSUS"),
            projectSubject.cursus_id
          )}
          {InfoRow(
            "dst-icon-book-open-02",
            translate(language, "PAGES.PROJECT_SUBJECT.MAIN.PROGRAM"),
            projectSubject.program?.name
          )}
          <div className="mt-4">
            <div className="s-regular my-3">
              <span className="dst-icon-menu-03 blue-font me-2" />
              {translate(language, "PAGES.PROJECT_SUBJECT.MAIN.DESCRIPTION.TITLE")}
            </div>
            <p className="mb-0 medium-gray-background p-3 rounded w-100 s-regular">
              {projectSubject.description || "—"}
            </p>
          </div>
        </Col>
        <Col md={6}>
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h3 className="mb-0 m-bold">{translate(language, "PAGES.PROJECT_SUBJECT.RESSOURCES.TITLE")}</h3>
            <DstButton
              variant="secondary"
              value={translate(language, "PAGES.PROJECT_SUBJECT.RESSOURCES.ADD")}
              btnImageBefore={<span className="dst-icon-plus me-2" />}
              clickFunction={() => {
                setEditingResource(null);
                setShowResourceModal(true);
              }}
            />
          </div>

          {!resourcesLoaded && (
            <Placeholder animation="glow">
              <Placeholder xs={12} className="mb-2" />
              <Placeholder xs={10} className="mb-2" />
              <Placeholder xs={8} />
            </Placeholder>
          )}

          {resourcesLoaded && resources.length === 0 && (
            <div className="text-muted s-regular">{translate(language, "PAGES.PROJECT_SUBJECT.RESSOURCES.EMPTY")}</div>
          )}

          {resourcesLoaded &&
            resources.map((resource) => (
              <div key={resource.id} className="d-flex justify-content-between align-items-center border-bottom py-2">
                <div className="d-flex align-items-start">
                  <span className="dst-icon-link-01 blue-font me-2 mt-1" />
                  <div>
                    <div className="m-regular">{resource.name}</div>

                    <div className="xs-regular text-muted">{resource.type}</div>

                    {resource.url && (
                      <a
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="s-regular text-muted text-decoration-underline"
                      >
                        {resource.url}
                      </a>
                    )}
                  </div>
                </div>

                <div className="d-flex gap-2">
                  <span
                    className="dst-icon-edit-02 clickable"
                    onClick={() => {
                      setEditingResource(resource);
                      setShowResourceModal(true);
                    }}
                  />
                  <span
                    className="dst-icon-trash-01 clickable text-danger"
                    onClick={() => _handleDeleteResource(resource.id)}
                  />
                </div>
              </div>
            ))}
        </Col>
      </Row>

      <EditProjectSubjectModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        subject={projectSubject}
        onUpdated={() => {
          if (!subject_id) return;

          fetchProjectSubjectDetails(Number(subject_id), setProjectSubject, setIsLoaded, language);
        }}
      />

      <EditProjectSubjectResourceModal
        show={showResourceModal}
        onHide={() => setShowResourceModal(false)}
        subjectId={projectSubject.id}
        resource={editingResource}
        onSaved={() => fetchProjectSubjectResources(projectSubject.id, setResources, setResourcesLoaded, language)}
      />

      <DstConfirm
        show={showConfirmDelete}
        onHide={() => {
          setShowConfirmDelete(false);
          setResourceToDelete(null);
        }}
        onConfirm={_confirmDeleteResource}
        title={translate(language, "PAGES.PROJECT_SUBJECT.RESSOURCES.CONFIRM.TITLE")}
        messageExplain={translate(language, "PAGES.PROJECT_SUBJECT.RESSOURCES.CONFIRM.EXPLAIN")}
        messageConfirm={translate(language, "PAGES.PROJECT_SUBJECT.RESSOURCES.CONFIRM.MESSAGE")}
        confirmTextButton={translate(language, "PAGES.PROJECT_SUBJECT.RESSOURCES.CONFIRM.BUTTON")}
      />
    </div>
  );
};

export default ProjectSubject;
